import { useEffect, useRef, useState, type FormEvent } from "react";
import { IconCheck, IconFileUpload, IconLoader2, IconSparkles, IconX } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserAuth } from "./auth";
import { clientId } from "./client-id";
import { capabilityEnabled, featureEnabled } from "./feature-access";
import { fileTitle, mimeTypeForFile } from "./source-files";
import type { ExistingPptDesignMode, PptTaskDetail } from "./outline-types";
import { businessTaskUploadForm, userApiWithUploadProgress } from "./new-ppt";

const OPEN_EVENT = "pptdeep:open-existing-ppt-beautify";
const MODES: Array<{ id: ExistingPptDesignMode; title: string; description: string }> = [
  { id: "enhanced", title: "内容增强", description: "保留核心文字，可优化表达与信息层级" },
  { id: "premium", title: "严格保留文字", description: "文字内容不改，只重做版式与视觉" },
  { id: "faithful", title: "忠实原稿", description: "文字与页面构图都尽量保持一致" },
];

export function openExistingPptBeautifyDialog(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export function ExistingPptBeautifyLauncher() {
  const auth = useUserAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<ExistingPptDesignMode>("enhanced");
  const [file, setFile] = useState<File>();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const authenticated = auth.state === "authenticated" && location.pathname.startsWith("/app") && !location.pathname.startsWith("/app/login");
  const imageModeAvailable = featureEnabled(auth.me?.features, "renderer.image2")
    && capabilityEnabled(auth.me?.features, "generation.existing_ppt_beautify");

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, show);
    return () => window.removeEventListener(OPEN_EVENT, show);
  }, []);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) setOpen(false);
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
      )].filter((element) => element.offsetParent !== null);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    window.setTimeout(() => dialogRef.current?.querySelector<HTMLElement>("button, input")?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus();
    };
  }, [open, busy]);

  if (!authenticated) return null;

  const chooseFile = (selected?: File) => {
    setError("");
    if (!selected) return setFile(undefined);
    if (!selected.name.toLowerCase().endsWith(".pptx")) return setError("请选择 .pptx 文件");
    if (selected.size > 30 * 1024 * 1024) return setError("文件不能超过 30 MB");
    setFile(selected);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!imageModeAvailable) return setError("现有 PPT 美化功能尚未向此账户开放");
    if (!file) return setError("请先选择 PPTX 文件");
    setBusy(true);
    setError("");
    try {
      const planning = {
        title: fileTitle(file.name),
        audience: "演示文稿目标受众",
        pageCountMode: "auto",
        deliveryPurpose: "balanced",
        narrativeMode: "briefing",
        visualStyle: "专业、克制、成品级视觉重设计",
        tone: "专业",
        scene: "一般",
        generationMode: "image",
        imageGenerationStrategy: "per_slide_hd",
        imageStylePreference: { style: "auto", strength: "prefer" },
        requestedThemePack: "smart-detect",
        workflowMode: "existing_ppt_beautify",
        existingPptDesignMode: mode,
      };
      const result = await userApiWithUploadProgress<PptTaskDetail & { businessTask?: { id: string } }>(
        "/api/app/v2/ppt-tasks",
        businessTaskUploadForm({
          inputKind: "pptx",
          idempotencyKey: clientId(),
          planning,
          async: true,
          attachments: [{
            kind: "document",
            originalName: file.name,
            mimeType: mimeTypeForFile(file, "pptx"),
            size: file.size,
          }],
        }, [file]),
        (value) => setProgress(value),
      );
      const taskId = result.businessTask?.id || result.task?.id;
      if (!taskId) throw new Error("任务已创建，但没有返回任务编号");
      setOpen(false);
      setFile(undefined);
      setProgress(0);
      navigate(`/app/projects/${encodeURIComponent(taskId)}/image2-generating`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "上传失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };

  return open && <div className="existing-ppt-launcher-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) setOpen(false);
    }}>
      <div className="existing-ppt-launcher-dialog" role="dialog" aria-modal="true" aria-labelledby="existing-ppt-launcher-title" ref={dialogRef}>
        <button className="existing-ppt-launcher-close" type="button" aria-label="关闭" disabled={busy} onClick={() => setOpen(false)}><IconX size={19}/></button>
        <header>
          <span><IconSparkles size={18}/> AI 美化现有 PPT</span>
          <h2 id="existing-ppt-launcher-title">上传原稿，先免费识别，再开始生成</h2>
          <p>解析、原页截图和主题识别不消耗积分。识别完成后才会核对生成所需积分。</p>
        </header>
        <form onSubmit={submit}>
          <fieldset disabled={busy}>
            <legend>选择美化模式</legend>
            <div className="existing-ppt-launcher-modes">{MODES.map((item) => <label className={mode === item.id ? "selected" : ""} key={item.id}>
              <input type="radio" name="existing-ppt-mode" value={item.id} checked={mode === item.id} onChange={() => setMode(item.id)}/>
              <span><strong>{item.title}</strong><small>{item.description}</small></span>
              {mode === item.id && <IconCheck size={18}/>}
            </label>)}</div>
          </fieldset>
          <button className={`existing-ppt-launcher-upload${file ? " has-file" : ""}`} type="button" disabled={busy} onClick={() => fileInputRef.current?.click()}>
            <IconFileUpload size={26}/>
            <span><strong>{file?.name || "选择 PPTX 文件"}</strong><small>{file ? `${(file.size / 1024 / 1024).toFixed(1)} MB · 点击可更换` : "最大 30 MB，上传和内容识别免费"}</small></span>
          </button>
          <input ref={fileInputRef} className="sr-only" type="file" accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation" onChange={(event) => chooseFile(event.target.files?.[0])}/>
          {busy && <div className="existing-ppt-launcher-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}><i style={{ width: `${progress}%` }}/><span>{progress < 96 ? `正在上传 ${progress}%` : "文件已上传，正在建立识别任务…"}</span></div>}
          {error && <p className="existing-ppt-launcher-error" role="alert">{error}</p>}
          <footer>
            <span>本步骤不扣积分</span>
            <button type="button" disabled={busy} onClick={() => setOpen(false)}>取消</button>
            <button className="primary" type="submit" disabled={busy || !file}>{busy ? <><IconLoader2 size={17}/>处理中</> : "上传并开始识别"}</button>
          </footer>
        </form>
      </div>
    </div>;
}
